import { runInAction, toJS } from 'mobx';
import { Request, Response } from 'System/utilities/network.js';

const BIOMETRIC_BANNER_NEVER_ASK_KEY = 'biometricBannerNeverAsk';
import langstore from 'System/i18n/translator.js';
import { deleteDB } from 'idb'
import { getParameterByName } from 'System/utilities/urls.js';
import { is_touch_device } from 'System/utilities/devices.js';
import {state as InitState} from "../state.js";
import {
    canUsePlatformAuth,
    isEnabled as biometricIsEnabled,
    setEnabled as biometricSetEnabled,
    getUserHandle as biometricGetUserHandle,
    createCredential as biometricCreate,
    getCredential as biometricGet,
    remove as biometricRemoveLocal,
    isSessionVerified,
    markSessionVerified,
    clearSessionVerified,
} from 'System/biometric.js';

import ReactGA from "react-ga4";

let data;
let articoli;
let _refreshPromise = null; // mutex: serializza richieste concorrenti di silent refresh

const init = async function (_token, welcomeMessage, internalUser) {

    let i;
    let loadingKey = 'appinit';

    // Biometric lock: il token è custodito da biometric.js, non caricare la sessione.
    // Mostra solo la lock screen nella login view.
    if (this.biometricLockActive) {
        return;
    }

    let token = this.userToken;
    let upgraded = await this.appUpgrade();

    if (token) {
        internalUser = this.userInfo.internal;
    }

    if (_token) {
        token = _token;
    }

    if (upgraded === true) {
        token = false;
    }

    this.loadingAdd(loadingKey);

    if (token && !internalUser) {
        const biometricNeverAsk = localStorage.getItem(BIOMETRIC_BANNER_NEVER_ASK_KEY) === '1';
        if (this.biometricAvailable && !this.biometricEnabled && !biometricNeverAsk) {
            runInAction(() => { this.biometricSetupPending = true; });
        }

        // carico risorse
        await Promise.all([
            this.updateLanguages(),
            this.updateMenu(),
            this.updateAutorizzazioni(),
            this.getUserSubscriptionNewsletter(),
            this.getInfoUtente(),
            this.updateStatiOrdine(),
            this.getBackEndConfigData(),
        ]);

        data = null;
        articoli = null;


        this.menuLoad();

        await this.languageChange(this.language);

        if (welcomeMessage) {
            this.notificationAdd(`${langstore.t("user_welcome", "Bentornato")} ${welcomeMessage}`, 'userformlogin');
        }

    } else {
        runInAction(() => {
            this.menus = [];
        });
        if (internalUser) {
            //TODO: test set lang for internal
            await this.languageChange(this.language);
            await this.getBackEndConfigData();
            await this.getMenuInternal(this.userInfo.userType);
            this.setUserRole();
        }
        await this.menuLoad();

        // this.languages
        await this.updateLanguages();

        let activeLanguages = this.languages;

        /*
          Settaggio lingua di default
          Se è già impstata una lingua la usa altrimenti
          assume che lingua segnalata nel config (tipicamente "it") esista sempre
        */
        let languageTemp = this.language ?? this.config.languages.languageDefault;

        /*
            Don't need to set a language if is already settled
         */
        if (this.language === null) {
            /*
              Cerca lingua di sistema;
              Se non è "it", lingua default diventa "en", ma solo se "en" esiste nelle lingue supportate
            */
            let languageSystem = navigator.language.split("-");
            languageSystem = languageSystem[0];
            if (languageSystem !== "it") {
                for (i = 0; i < activeLanguages.length; i++) {
                    if ("en" === activeLanguages[i].iso) {
                        languageTemp = activeLanguages[i].iso;
                    }
                }
            }

            /*
              Se lingua di sistem esiste nella lista lingue, scelgo quella
              Sovrascrivo lingua default
            */
            for (i = 0; i < activeLanguages.length; i++) {
                if (languageSystem === activeLanguages[i].iso) {
                    languageTemp = activeLanguages[i].iso;
                }
            }

            /*
              Se c'è una lingua in cache ed esiste nella lista lingue, scelgo quella
              Sovrascrive lingua sistema
            */
            if (this.language) {
                for (i = 0; i < activeLanguages.length; i++) {
                    if (this.language === activeLanguages[i].iso) {
                        languageTemp = activeLanguages[i].iso;
                    }
                }
            }
        }

        if (!internalUser) {
            await this.languageChange(languageTemp);
            if (welcomeMessage) {
                this.notificationAdd(langstore.t("user_welcome", "Bentornato" + " " + welcomeMessage), 'userformlogin');
            }
        }
    }

    runInAction(() => {
        this.appReady = true;
        this.loadingRemove(loadingKey);
    });

    // this.routeReload(); // serve?
}

const initCore = function () {
    this.uiTouch = is_touch_device();
}

const clearStorage = function (appIndex) {
    Object.keys(localStorage).forEach(lsk => {
        if (lsk.indexOf(`${appIndex}_`) === 0) {
            localStorage.removeItem(lsk);
        }
    });
}

const menuLoad = function () {
    let menus = toJS(this.menus)
    let routes = toJS(this.routes);
    //console.log(menus)
    if (menus) {
        for (var i = 0; i < menus.length; i++) {
            let menu = menus[i];
            let routeAdded = false;
            for (var ii = 0; ii < routes.length; ii++) {
                let id = routes[ii].id;
                if (routes[ii].path === menu.path) {
                    menus[i].routeId = id;
                    break;
                }
            }
        }
    }

    runInAction(() => {
        this.menus = menus;
    });
}

const userLogin = async function (microsoftToken = null) {
    let loadingKey = 'userlogin';
    let url = this.config.paths.apiURL + "profilo/login";
    let userName = this.formData.loginEmail;
    let userPassword = this.formData.loginPassword;

    // Fallback if user does not enter "PC-" suffix
    // TODO remove and replace with a proper user toast
    if (this.config.brand === 'primacucine' && !userName.startsWith('PC-') && !userName.includes('@')) {
        userName = 'PC-' + userName;
    }

    let params = { username: userName, password: userPassword };

    if (microsoftToken) {
        params.microsoftToken = microsoftToken;
        params.password = 'null'
    }

    // spinner
    this.loadingAdd(loadingKey);

    // API call
    let response = await this.dataLayer({
        url: url,
        cacheAge: 0,
        params: params,
    });

    await this.userAuth(response, loadingKey);
}

const trySsoLogin = async function (token) {
    const sso_token = token && token.length > 0 ? token : getParameterByName('ssoToken');

    if (sso_token) {
        runInAction(() => {
            this.ssoLoginRunning = true;
        });

        let upgraded = await this.appUpgrade();
        if (upgraded === true) {
            this.userToken = null;
        }

        let loadingKey = 'userlogin';
        let url = this.config.paths.apiURL + "profilo/ssoLogin";
        let params = { sso_token }

        this.loadingAdd(loadingKey);

        let response = await this.dataLayer({
            url: url,
            cacheAge: 0,
            params: params,
        });

        let login = await this.userAuth({
            data: response.data
        });

        this.routeChange(this.config.routes.index);
        this.loadingRemove(loadingKey);

        runInAction(() => {
            if (response) {
                this.ssoLoginRunning = false;
            }
        });
    }
}

const userAuth = async function (response, loadingKey) {

    // prevent junk errors if missing data from response
    if (typeof response.data === 'undefined') {
        console.error(`ERROR: userAuth ${response}`);
        return;
    }

    // business logic
    runInAction(() => {
        if (response) {
            this.dataOrder = response.data.dettaglioOrdine;
        }
        if (loadingKey)
            this.loadingRemove(loadingKey);
    });

    // data management
    runInAction(() => {
        if (response) {
            let profilo = response.data.profilo;

            this.userInfo = {
                c_utente: profilo.c_utente,
                c_cliente_gruppo: profilo.c_cliente_gruppo || null,
                surname: profilo.cognome,
                name: profilo.ds_utente,
                fax: profilo.fax,
                username: profilo.username || null,
                phone: profilo.telefono,
                services: profilo.services,
                internal: profilo.isInternal || false,
                commercialPersonID: profilo.agente && profilo.agente.c_agente ? profilo.agente.c_agente : null,
                cNomeAgenzia: profilo.cNomeAgenzia,
                allCommercialPersonID: profilo.agenteAllCodes || [],
                isCapoArea: profilo.agente && profilo.agente.fg_capoarea || false,
                isAgente: profilo.agente && profilo.agente.fg_agente || false,
                isSubAgente: profilo.agente && profilo.agente.fg_subagente || false,
                isExpoUser: profilo.isCommleTeamExpoUser || false,
                isCommleDataViewer: profilo.isCommleDataViewer || false,
                isCommleAMAssistant: profilo.isCommleAMAssistant || false,
                isAgenteEstero: profilo.agente && profilo.agente.fg_estero || false,
                impersonatingFrom: profilo.impersonificatingFrom || false,
                fastconfirm: profilo.fastconfirm || false,
                pricelists: profilo.listini_abilitati || null,
                userType: profilo.agente ? '2' : '1',
                canImpersonificate: profilo.canImpersonificate || false,
                canSendAcademyNewsletter: profilo.canSendAcademyNewsletter || false,
                canCheckOrders: profilo.canCheckOrders || false,
                canImpersonificateInternalAgents: profilo.canImpersonificateInternalAgents || false,
                canReceivePushNotifications: !!this.config?.pushNotifications?.enabled
                    && (profilo.pushNotificationsTypesEnabled || []).length > 0,
                pushNotificationsTypesEnabled: profilo.pushNotificationsTypesEnabled || []
            };
            this.userToken = response.data.token;

            // token fresco dal server = sessione verificata, evita biometric lock al prossimo reload
            markSessionVerified();

            // login andato a buon fine: rilascia il lock biometrico per non bloccare init()
            this.biometricLockActive = false;

            // banner richiesta login biometrico dopo il primo login, se disponibile e non ancora attivo
            const biometricNeverAsk = localStorage.getItem(BIOMETRIC_BANNER_NEVER_ASK_KEY) === '1';
            if (this.biometricAvailable && !this.biometricEnabled && !biometricNeverAsk) {
                this.biometricSetupPending = true;
            }

            // QUICK FIX TO BE CHECKED
            if (profilo.linguaISO) {
                if (profilo.linguaISO.trim() === '*') {
                    this.language = this.config.languages.languageDefault
                } else {
                    this.language = profilo.linguaISO
                }
            }

            this.userService = profilo.currentService;

            if (loadingKey)
                this.loadingRemove(loadingKey);

            if (this.config.development.production && this.config.vendors.googleAnalyticsV4ID) {
                ReactGA.event({
                    category: "user",
                    action: "user logged in",
                    label: "user logged in", // optional
                });
            }

            // init
            this.init(response.data.token, profilo.ds_utente, profilo.isInternal || false);
        }
    });

    void this.pwaInstallLog();

    if (response?.data?.refreshToken) {
        runInAction(() => { this.userRefreshToken = response.data.refreshToken; });
    }
}

const userLogout = async function () {

    if (this.userToken) {

        var store = this;
        var requestBody = new Request();
        var request = {
            method: 'post', headers: {
                "Content-Type": "application/json",
                "x-access-token": store.userToken
            }
        };
        var url = store.config.paths.apiURL + "profilo/logout";

        request.body = JSON.stringify(requestBody);

        fetch(url, request).then(function (response) {
            return response.json();
        }).then(function (result) {
            var response = new Response(result);
        });

        this.routeRedirect = null; // I need to empty it befero RunInAction to prevent unwanted API call on logout

        this.notificationAdd(langstore.t("user_goodbye", "Utente scollegato"), 'userreset');
        clearSessionVerified(); // forza nuova verifica biometrica al prossimo accesso
        runInAction(() => { this.userRefreshToken = null; });

        if (this.config.development.production && this.config.vendors.googleAnalyticsV4ID) {
            ReactGA.event({
                category: "user",
                action: "user logged out",
                label: "user logged out", // optional
            });
        }
    }

    const loadingKey = "disconnecting";


    this.loadingAdd(loadingKey);

    if (this.networkDBStore) {
        await this.networkDBStore.clear('networkdata');
    }

    let indexCurrent = localStorage.getItem(`app_index_${this.namespaceIndex}`) || "__new__";

    clearStorage(indexCurrent);

    this.loading = [];
    // DEPRECATED - Always logout redirect to /login
    // if (!getParameterByName('ssoToken')) {
    //     this.route = "/login";
    // }

    this.loadingRemove(loadingKey);

    runInAction(() => {
        this.userToken = null;
        this.userService = null;
        this.userTokenRoot = null;
        this.uerInfoRoot = {};
        this.userServiceRoot = null;
        this.formData = {};
        this.menus = null;
        this.enable_routes = [];
        this.cacheParams = null;
        this.userInfo = {
            name: "",
            email: "",
            title: ""
        };
        this.dataUserDetails = {};
        this.dataAutorizzazioni = {};
        this.selfCliente = null;
        this.dataNoteTecnicheDashboard = {
            loading: true,
            messaggi_da_leggere: 0,
            messaggi_da_gestire: 0
        };
        this.dataOrdersToConfirmDashboard = {
            loading: true,
            count: 0
        };
        this.dataWarrantiesToBeActivatedDashboard = {
            loading: true,
            count: 0
        };
        this.dataAcademyVideoDashboard = {
            loading: true,
            count: 0
        };
        this.dataEcomm = {};
        this.dataCommerciale = InitState.dataCommerciale;
    });

}

const userReset = async function () {
    runInAction(() => {
        this.appReset();
        this.userLogout();
    });
}

const userProjectionClose = function () {

    let rootToken = this.userTokenRoot;
    let rootInfo = this.uerInfoRoot;
    let rootService = this.userServiceRoot;

    runInAction(() => {
        this.userToken = rootToken;
        this.uerInfoRoot = rootInfo;
        this.userServiceRoot = rootService;
        this.userTokenRoot = null;
        this.uerInfoRoot = {};
        this.userServiceRoot = null;
    });

}

const userProjectionOpen = function () {

    let userToken = this.userTokenRoot;
    let userInfo = this.uerInfoRoot;
    let userService = this.userServiceRoot;

    runInAction(() => {
        // projected as self until new selection is made
        // this.userToken = userToken;
        // this.uerInfoRoot = userInfo;
        // this.userServiceRoot = userService;

        this.userTokenRoot = userToken;
        this.userInfoRoot = userInfo;
        this.userServiceRoot = userService;
    });

}

const languageChange = async function (language, notify) {

    this.loadingAdd("languageChange");

    var store = this;
    var requestBody = new Request({ data: { language: language } });
    var request = {
        method: 'post', headers: {
            "Content-Type": "application/json",
            "x-access-token": store.userToken
        }
    };
    var url = store.config.paths.apiURL + "profilo/setlingua";

    request.body = JSON.stringify(requestBody);

    fetch(url, request).then(function (response) {
        return response.json();
    }).then(function (result) {
        var response = new Response(result);
    });

    runInAction(() => {
        this.language = language;
    });


    // get language bundle

    const token = this.userToken;
    let params = {
        "lingua": [language]
    }
    let bundle = {};
    let response = await this.dataLayer({
        url: this.config.paths.apiURL + "languages/traduzioni",
        cacheAge: 0,
        params: params,
        userToken: token
    });

    if (response && response.data && response.data.traduzioni && response.data.traduzioni[language]) {
        bundle = response.data.traduzioni[language];
        langstore.addLanguageBundle(language, bundle);
        langstore.changeLanguage(language);
    }

    if (token) {
        //await this.updateStatiOrdineAttivo();
        //await this.updateStatiReclamo()
        await this.updateStatiOrdine();
        await this.getUltimoRunOk();
    }

    this.loadingRemove("languageChange");

    return true;

}

const passwordReset = async function () {
    let loadingKey = 'passwordReset';
    let url = this.config.paths.apiURL + "password/reset";

    // spinner
    this.loadingAdd(loadingKey);

    // API call
    let response = await this.dataLayer({
        url: url,
        cacheAge: 0,
        params: {
            c_utente: this.formData.loginID.toUpperCase(),
        },
        rawResponse: true
    });

    this.loadingRemove(loadingKey);

    // business logic
    runInAction(() => {
        this.formData = {};
        if (response) {
            if (response.status === "OK") {
                this.modalOpen("ModalReset", {
                    title: langstore.t("user_resetpw_title", "Istruzioni inviate"),
                    content: langstore.t("user_resetpw_body", "Controlla la casella email, abbiamo inviato le istruzioni per procedere al reset della password all'indirizzo "),
                    email: response.data.confirmationEmail
                });
            } else {
                this.modalOpen("ModalReset", {
                    title: langstore.t("user_resetpwerrortitle", "C'è stato un problema"),
                    content: response.message
                });
            }
        }

        this.loadingRemove(loadingKey);

    });

}

const startPasswordReset = async function () {
    let loadingKey = 'passwordResetProcedure';
    let url = this.config.paths.apiURL + "password/reset/check";
    let resetToken = getParameterByName('token')
    this.loadingAdd(loadingKey);

    runInAction(() => {
        this.pswreset = undefined;
        this.pswresetMessage = null;
    })

    if (resetToken !== null && resetToken !== "") {

        // API call
        let response = await this.dataLayer({
            url: url,
            cacheAge: 0,
            params: {
                resetToken: resetToken
            }
        });

        if (response && response.data) {
            runInAction(() => {
                this.pswreset = {
                    c_utente: response.data.c_utente
                };
            })
        }
        else {
            runInAction(() => {
                this.pswreset = null;
                this.pswresetMessage = response;
            })
        }

        /*if (response && response.data && response.data.c_utente && response.status && response.status === "OK") {
            this.formData = {
                pswreset: {
                    c_utente: response.data.c_utente
                }
            };
        } else {
            this.notificationAdd(
                langstore.t("password_reset_token_invalid", "Token non valido"),
                "pswresetko",
                2000,
                "error"
            );
            this.routeChange('/login');
        }*/

        this.loadingRemove(loadingKey);
    } else {
        // no token in URL: show error view instead of hanging on blank state
        runInAction(() => {
            this.pswreset = null;
        })
        this.loadingRemove(loadingKey);
    }
}

const passwordResetConfirm = async function (data) {
    let loadingKey = 'passwordResetProcedureConfirm';
    let url = this.config.paths.apiURL + "password/reset/confirm";
    let resetToken = getParameterByName('token')

    this.loadingAdd(loadingKey);

    if (resetToken !== null && resetToken !== "") {

        // API call
        let response = await this.dataLayer({
            url: url,
            cacheAge: 0,
            params: {
                ...data,
                resetToken: resetToken,
                c_utente: this.pswreset.c_utente
            }
        });

        if (response.status === "OK") {
            this.formData = toJS({});
            this.notificationAdd(
                langstore.t("password_reset_confirm_ok", "Procedura di reset password completata con successo"),
                "pswresetok",
                2000,
                "success"
            );
            this.routeChange('/login');
        }

        this.loadingRemove(loadingKey);
    }
}

const appReset = async function (appIndex) {
    // console.log("deleting db", appIndex);

    // delete localstorage (all)
    clearStorage(appIndex);
    //localStorage.clear();

    // delete inxedDB (all)
    if (appIndex) {
        await deleteDB(appIndex, {});
    }
    return true;
}

const appUpgrade = async function () {

    // get current version
    let indexCurrent = localStorage.getItem(`app_index_${this.namespaceIndex}`) || "__new__";

    // get config version
    let indexNew = this.fullNamespace;

    // same version or unprotected route, do nothing
    if (indexNew === indexCurrent || this.routeParamsCurrent.unproctected === true) {
        return false;
    }

    // process different version
    await this.userLogout();
    await this.appReset(indexCurrent);
    this.log(`upgraded from ${indexCurrent} to ${indexNew}`, "OK");

    localStorage.setItem(`app_index_${this.namespaceIndex}`, String(indexNew));
    return true;
}


// ─── Azioni biometriche ──────────────────────────────────────────────────────

/**
 * Chiamare PRIMA di init() all'avvio dell'app.
 * Se l'utente ha biometria registrata e nessuna sessione verificata recente,
 * attiva il lock screen: il token sarà emesso dal server dopo verifica biometrica.
 */
const biometricInit = async function () {
    const available = await canUsePlatformAuth();
    const enabled = biometricIsEnabled() && available;

    runInAction(() => {
        this.biometricAvailable = available;
        this.biometricEnabled = enabled;
    });

    if (!enabled) return;
    if (isSessionVerified()) return;

    // Sessione non verificata: forza biometric login. Nessun token viene emesso
    // finché la firma WebAuthn non è validata dal backend.
    runInAction(() => {
        this.userToken = null;
        this.biometricLockActive = true;
        this.menus = [];
    });
};

/**
 * Login WebAuthn server-side: due round-trip (options + verify).
 * Il backend emette un nuovo token dopo aver verificato la firma.
 */
const biometricLogin = async function () {
    try {
        const userId = biometricGetUserHandle();
        if (!userId) throw new Error('no_user_handle');

        const baseUrl = this.config.paths.apiURL + 'auth/biometric/login';

        // 1. Richiedi le options al server
        const optsRes = await fetch(`${baseUrl}/options`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: { c_utente: userId } }),
        });
        const optsJson = await optsRes.json();

        if (optsJson.status !== 'OK') throw new Error('options_failed');

        // 2. Firma con WebAuthn (richiede gesto utente)
        const credential = await biometricGet(optsJson.data);

        // 3. Verifica la firma sul server e ricevi token + profilo
        const verifyRes = await fetch(`${baseUrl}/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: { c_utente: userId, credential } }),
        });
        const verifyJson = await verifyRes.json();

        if (verifyJson.status !== 'OK') throw new Error('verify_failed');

        if (!verifyJson.data?.token) throw new Error('invalid_token');

        markSessionVerified();

        runInAction(() => {
            this.biometricLockActive = false;
        });

        await this.userAuth(verifyJson);
    } catch (e) {
        if (e.name === 'NotAllowedError') {
            // L'utente ha annullato il prompt biometrico
            return;
        }
        // Errore reale: bypass al form di login standard.
        // NON cancello la registrazione: l'utente potrà riprovare.
        runInAction(() => {
            this.biometricLockActive = false;
        });
        this.notificationAdd(
            langstore.t('biometric_failed', 'Accesso biometrico non disponibile, usa le credenziali'),
            'biometric_fail',
            5000,
            'error'
        );
    }
};

/**
 * Registra una credenziale WebAuthn lato server.
 * L'utente deve essere loggato. Backend salva la public key associata a c_utente.
 */
const biometricRegister = async function () {
    try {

        const accepted = await new Promise(resolve => {
            const modalAcceptTet = `<h1 class="!mb-8">${langstore.t('modal_biometric_accept_text', 'Scegliendo di accedere tramite autenticazione biometrica, i tuoi dati biometrici verranno gestiti esclusivamente dal tuo dispositivo e non saranno in alcun modo trasmessi')}</h1>`
            this.modalOpen("ModalGenericText", {
                text: modalAcceptTet,
                buttons: [
                    {
                        label: 'RIFIUTA',
                        className: 'medium_size reject_filled',
                        onClick: () => { this.modalClose(); resolve(false); },
                    },
                    {
                        label: 'ACCETTA',
                        className: 'medium_size accept_filled',
                        onClick: () => { this.modalClose(); resolve(true); },
                    },
                ],
            });
        });

        if (!accepted) {
            this.biometricNeverAsk();
            return;
        }

        const userId = this.userInfo?.c_utente;
        if (!userId) throw new Error('no_user');

        const baseUrl = this.config.paths.apiURL + 'auth/biometric/register';
        const headers = {
            'Content-Type': 'application/json',
            'x-access-token': this.userToken,
        };

        // Cancella eventuali credenziali residue lato server: evita InvalidStateError
        // quando l'authenticator riceve una vecchia cred in excludeCredentials.
        try {
            await fetch(this.config.paths.apiURL + 'auth/biometric/credential/delete', {
                method: 'POST', headers, body: JSON.stringify({ data: {c_utente: userId} }),
            });
        } catch (_) { /* best effort */ }

        // 1. Options dal server (challenge + user info per WebAuthn)
        const optsRes = await fetch(`${baseUrl}/options`, {
            method: 'POST', headers, body: JSON.stringify({}),
        });

        const optsJson = await optsRes.json();
        if (optsJson.status !== 'OK') throw new Error('options_failed');

        // 2. Crea la credenziale (richiede gesto utente)
        const credential = await biometricCreate(optsJson.data);

        // 3. Verifica e salva sul server
        const verifyRes = await fetch(`${baseUrl}/verify`, {
            method: 'POST', headers, body: JSON.stringify({ data: { credential } }),
        });

        const verifyJson = await verifyRes.json();

        if (verifyJson.status !== 'OK') throw new Error('verify_failed');

        biometricSetEnabled(userId);
        markSessionVerified();
        runInAction(() => {
            this.biometricEnabled = true;
            this.biometricSetupPending = false;
        });
        this.notificationAdd(
            langstore.t('biometric_enabled', 'Accesso biometrico attivato'),
            'biometric_ok',
            3000,
            'success'
        );
    } catch (e) {
        if (e.name === 'NotAllowedError') {
            runInAction(() => { this.biometricSetupPending = false; });
            return;
        }
        this.notificationAdd(
            langstore.t('biometric_setup_failed', "Impossibile attivare l'accesso biometrico"),
            'biometric_setup_fail',
            4000,
            'error'
        );
    }
};

/**
 * Disabilita la richiesta di uso login biometrico
 * Si reimposta a true al reload della PWA
 */
const biometricTemporaryDisabled = async function () {
    runInAction(() => {
        this.biometricSetupPending = false;
    });
};

const biometricNeverAsk = function () {
    try { localStorage.setItem(BIOMETRIC_BANNER_NEVER_ASK_KEY, '1'); } catch (_) {}
    runInAction(() => {
        this.biometricSetupPending = false;
    });
};

/**
 * Bypassa il lock biometrico e torna al form di login standard.
 * Non rimuove la registrazione biometrica: al prossimo accesso verrà riproposta.
 * Se era attivo il relock (sessione scaduta), esegue anche il logout.
 */
const biometricBypass = function () {
    const wasRelocked = this.biometricRelockActive;
    runInAction(() => {
        this.biometricLockActive = false;
        this.biometricRelockActive = false;
    });
    if (wasRelocked) {
        this.userLogout();
    }
};

/**
 * Attiva il relock overlay: mostrato quando biometricSilentRefresh viene annullato.
 * L'utente può riprovare la biometria o fare logout verso il form di login.
 */
const biometricRelock = function () {
    runInAction(() => {
        this.biometricRelockActive = true;
    });
};

/**
 * Riprova il silent refresh dall'overlay di relock.
 * Se riesce, chiude il relock senza perdere la sessione corrente.
 */
const biometricRelockRetry = async function () {
    const result = await this.biometricSilentRefresh();
    if (result.success) {
        runInAction(() => {
            this.biometricRelockActive = false;
        });

        // Se il relock è scattato durante init() (token scaduto,
        // sessione biometrica ancora "verificata" entro TTL), le chiamate di
        // caricamento risorse sono fallite: autorizzazioni e profilo vuoti.
        // Senza ricaricarle l'app resta inconsistente (menu incompleto,
        // dashboard senza card) finché l'utente non fa refresh manuale.
        const coreDataMissing =
            !this.dataUserDetails ||
            Object.keys(this.dataUserDetails || {}).length === 0 ||
            Object.keys(this.dataAutorizzazioni || {}).length === 0;

        if (coreDataMissing) {
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches
                || navigator.standalone === true;
            if (isStandalone) {
                // token fresco già persistito da biometricSilentRefresh:
                // il reload ripercorre il cold start, flusso sempre affidabile
                window.location.reload();
                return;
            }
            await this.init(this.userToken);
        // Aggiorna le credenziali push nell'IDB con il token fresco così il SW
        // può ri-sottoscrivere autonomamente se FCM ruota il token (pushsubscriptionchange).
        if (this.pushNotificationInit) {
            this.pushNotificationInit().catch(() => {});
        }
        }
    }
};

/**
 * Rimuove la registrazione biometrica: notifica server e cancella stato locale.
 * Best effort lato server: se il delete fallisce, lo stato locale viene comunque azzerato.
 */
const biometricRemove = async function () {
    const userId = biometricGetUserHandle();
    try {
        if (userId && this.userToken) {
            await fetch(this.config.paths.apiURL + 'auth/biometric/credential/delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': this.userToken,
                },
                body: JSON.stringify({ data: {c_utente: userId} }),
            });
        }
    } catch (_) { /* best effort */ }

    biometricRemoveLocal();
    runInAction(() => {
        this.biometricEnabled = false;
        this.biometricLockActive = false;
        this.biometricSetupPending = false;
    });
    this.notificationAdd(
        langstore.t('biometric_removed', 'Accesso biometrico disattivato'),
        'biometric_removed',
        3000,
        'info'
    );
};

/**
 * Re-autentica via biometria quando il token corrente è scaduto/invalido.
 * Esegue un biometric login completo (options + verify), aggiorna lo store.
 * Mutex (_refreshPromise) per serializzare chiamate concorrenti.
 * @returns {{ success: boolean, reason?: string }}
 */
const biometricSilentRefresh = async function ()
{
    if (_refreshPromise) return _refreshPromise;

    const self = this;
    _refreshPromise = (async () => {
        try {
            runInAction(() => { self.biometricRefreshPending = true; });

            const userId = biometricGetUserHandle();
            if (!userId) return { success: false, reason: 'no_user_handle' };

            const baseUrl = self.config.paths.apiURL + 'auth/biometric/login';

            const optsRes = await fetch(`${baseUrl}/options`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: { c_utente: userId } }),
            });
            const optsJson = await optsRes.json();
            if (optsJson.status !== 'OK') return { success: false, reason: 'options_failed' };

            const credential = await biometricGet(optsJson.data);

            const verifyRes = await fetch(`${baseUrl}/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: { c_utente: userId, credential } }),
            });
            const verifyJson = await verifyRes.json();
            if (verifyJson.status !== 'OK') return { success: false, reason: 'verify_failed' };

            const newToken = verifyJson.data?.token;
            if (!newToken) return { success: false, reason: 'invalid_response' };

            markSessionVerified();
            runInAction(() => {
                self.userToken = newToken;
                if (verifyJson.data.refreshToken) {
                    self.userRefreshToken = verifyJson.data.refreshToken;
                }
            });
            return { success: true };
        } catch (e) {
            if (e.name === 'NotAllowedError') {
                // L'utente ha annullato il prompt biometrico
                return { success: false, reason: 'user_cancelled' };
            }
            runInAction(() => { self.userRefreshToken = null; });
            return { success: false, reason: 'error' };
        } finally {
            _refreshPromise = null;
            runInAction(() => { self.biometricRefreshPending = false; });
        }
    })();

    return _refreshPromise;
};

export {
    initCore,
    init,
    userLogin,
    userAuth,
    trySsoLogin,
    userLogout,
    menuLoad,
    userReset,
    userProjectionClose,
    languageChange,
    passwordReset,
    appUpgrade,
    appReset,
    startPasswordReset,
    passwordResetConfirm,
    biometricInit,
    biometricLogin,
    biometricRegister,
    biometricTemporaryDisabled,
    biometricNeverAsk,
    biometricBypass,
    biometricRelock,
    biometricRelockRetry,
    biometricRemove,
    biometricSilentRefresh,
    data,
    articoli
}

export default {
    data,
    articoli
}
